import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { authService } from './services/index.js'
import { mobileAuthService } from '../../mobile/services/mobileAuthService.js'
import { authContextRef } from './authContextRef.js'
import { USE_JWT_COOKIE } from './authConfig.js'
import { useLocation } from 'react-router-dom'
import { MessageDialog } from '../ui'

const AuthContext = createContext()

const initialState = {
  user: null,
  pixId: null,
  userKyotenId: null,
  objectIdAutoFlg: null,
  userAuthority: null,
  hihyojiFlg: null,
  // mobileユーザー
  pixStorageFlg: null,
  userName: null,
  serialNo: null,
  isSaveLocal: null,

  isAuthenticated: false,
  isLoading: true,
  error: null
}

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null
      }
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload?.user ?? state.user,
        pixId: action.payload?.pixId ?? state.pixId,
        userKyotenId: action.payload?.userKyotenId ?? state.userKyotenId,
        objectIdAutoFlg: action.payload?.objectIdAutoFlg ?? state.objectIdAutoFlg,
        userAuthority: action.payload?.userAuthority ?? state.userAuthority,
        hihyojiFlg: action.payload?.hihyojiFlg ?? state.hihyojiFlg,
        pixStorageFlg: action.payload?.pixStorageFlg ?? state.pixStorageFlg,
        userName: action.payload?.userName ?? state.userName,
        serialNo: action.payload?.serialNo ?? state.serialNo,
        isSaveLocal: action.payload?.isSaveLocal ?? state.isSaveLocal,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        pixId: null,
        userKyotenId: null,
        objectIdAutoFlg: null,
        userAuthority: null,
        hihyojiFlg: null,
        pixStorageFlg: null,
        userName: null,
        serialNo: null,
        isSaveLocal: false,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        pixId: null,
        userKyotenId: null,
        objectIdAutoFlg: null,
        userAuthority: null,
        hihyojiFlg: null,
        pixStorageFlg: null,
        userName: null,
        serialNo: null,
        isSaveLocal: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      }
    default:
      return state
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const location = useLocation()

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('token')
      // With cookie auth we may have no token in storage (cookie only); still run validateToken() to check cookie.
      const hasTokenOrCookie = !!token || USE_JWT_COOKIE

      if (location.pathname === '/login') return
      if (location.pathname === '/riyoToroku') return
      if (location.pathname === '/share') return
      if (location.pathname === '/appLogin') return
      if (location.pathname === '/appKaiinToroku') return
      if (location.pathname === '/appSendResetPassword') return
      if (location.pathname === '/appResetPassword') return
      if (location.pathname === '/appInquiry') return
      if (location.pathname === '/appShare') return

      if (location.pathname === '/inquiry') {
        if (!hasTokenOrCookie) {
          dispatch({ type: 'AUTH_FAILURE', payload: { error: null } })
          return
        }
      }

      if (location.pathname.startsWith('/app')) {
        // mobile画面の認証チェック
        if (!hasTokenOrCookie) {
          // mobile画面ではダイアログを表示
          MessageDialog.show(
            '認証エラー',
            'ログイン、または再ログインしてください。',
            () => {
              // OKボタン押下後の処理（必要に応じて追加）
            },
            'error'
          )
          dispatch({ type: 'AUTH_FAILURE', payload: { error: 'ログイン、または再ログインしてください。' } })
          return
        }

        try {
          dispatch({ type: 'AUTH_START' })
          const response = await mobileAuthService.validateToken()
          if (response.returnCode !== '200') {
            localStorage.removeItem('token')
            // mobile画面ではダイアログを表示
            MessageDialog.show(
              '認証エラー',
              '認証トークンに問題があります。再ログインしてください。',
              () => {
                // OKボタン押下後の処理（必要に応じて追加）
              },
              'error'
            )
            dispatch({
              type: 'AUTH_FAILURE',
              payload: { error: '認証トークンに問題があります。再ログインしてください。' }
            })
            return
          }
          dispatch({ type: 'AUTH_SUCCESS', payload: { user: response.userId || 'mobile', pixId: response.pixId, userKyotenId: response.userKyotenId, objectIdAutoFlg: response.objectIdAutoFlg, userAuthority: response.userAuthority, hihyojiFlg: response.hihyojiFlg, pixStorageFlg: response.pixStorageFlgBase, userName: response.userNameBase, serialNo: response.serialNoBase, isSaveLocal: response.isSaveLocalBase } })
        } catch (error) {
          localStorage.removeItem('token')
          // mobile画面ではダイアログを表示
          MessageDialog.show(
            '認証エラー',
            '認証トークンに問題があります。再ログインしてください。',
            () => {
              // OKボタン押下後の処理（必要に応じて追加）
            },
            'error'
          )
          dispatch({
            type: 'AUTH_FAILURE',
            payload: { error: '認証トークンに問題があります。再ログインしてください。' }
          })
        }
        return
      }

      // PC画面の認証チェック
      if (!hasTokenOrCookie) {
        dispatch({ type: 'AUTH_FAILURE', payload: { error: 'ログイン、または再ログインしてください。' } })
        return
      }

      try {
        dispatch({ type: 'AUTH_START' })
        const response = await authService.validateToken()
        if (response.returnCode !== '200') {
          localStorage.removeItem('token')
          dispatch({
            type: 'AUTH_FAILURE',
            payload: { error: '認証トークンに問題があります。再ログインしてください。' }
          })
          return
        }
        dispatch({ type: 'AUTH_SUCCESS', payload: { user: response.userId, pixId: response.pixId, userKyotenId: response.userKyotenId, objectIdAutoFlg: response.objectIdAutoFlg, userAuthority: response.userAuthority, hihyojiFlg: response.hihyojiFlg, pixStorageFlg: response.pixStorageFlgBase, userName: response.userNameBase, serialNo: response.serialNoBase, isSaveLocal: response.isSaveLocalBase } })
      } catch (error) {
        localStorage.removeItem('token')
        dispatch({
          type: 'AUTH_FAILURE',
          payload: { error: '認証トークンに問題があります。再ログインしてください。' }
        })
      }
    }
    validateToken()
  }, [location.pathname])

  useEffect(() => {
    authContextRef.current = { dispatch }
    return () => { authContextRef.current = null }
  }, [dispatch])

  const login = async (credentials) => {
    try {
      dispatch({ type: 'AUTH_START' })
      
      // PC版とモバイル版で異なる認証サービスを使用
      let response
      if (credentials.deviceId) {
        // モバイル版: デバイスIDがある場合
        response = await mobileAuthService.login(credentials)
      } else {
        // PC版: デバイスIDがない場合
        response = await authService.login(credentials)
      }

      if (response.successFlg === '1') {
        if (response.token) {
          if (!USE_JWT_COOKIE) {
            localStorage.setItem('token', response.token)
          }
        }
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: response.userId, pixId: response.pixId, userKyotenId: response.userKyotenId, objectIdAutoFlg: response.objectIdAutoFlg, userAuthority: response.userAuthority, hihyojiFlg: response.hihyojiFlg, pixStorageFlg: response.pixStorageFlgBase, userName: response.userNameBase, serialNo: response.serialNoBase, isSaveLocal: response.isSaveLocalBase }
        })
        return response
      } else {
        dispatch({
          type: 'AUTH_FAILURE',
          payload: { error: response.errMessage }
        })
        throw new Error(response.errMessage || 'ログインが失敗しました。')
      }
    } catch (error) {
      // Because if even failed backend return 200, it wont come here
      dispatch({
        type: 'AUTH_FAILURE',
        payload: { error: error.message }
      })
      throw error
    }
  }

  const logout = async () => {
    // try {
    //   await authService.logout()
    // } catch (error) {
    //   console.error('Logout error:', error)
    // } finally {
    authService.logout()
    dispatch({ type: 'LOGOUT' })
    //}
  }

  const value = {
    ...state,
    login,
    logout,
    dispatch
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
